<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import AccountAPI from 'dashboard/api/account';
import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Tabs from 'dashboard/components/ui/Tabs/Tabs.vue';
import TabsItem from 'dashboard/components/ui/Tabs/TabsItem.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const DOCS = [
  {
    key: 'terms',
    tabKey: 'BILLING_SETTINGS.TERMS.TAB_TERMS',
    acceptedAttr: 'terms_accepted_at',
    url: 'https://rookoo.ai/trust/ROOKOO%20GENERAL%20TERMS%20AND%20CONDITIONS%20V1.1.pdf#toolbar=0&navpanes=0',
  },
  {
    key: 'dpa',
    tabKey: 'BILLING_SETTINGS.TERMS.TAB_DPA',
    acceptedAttr: 'dpa_accepted_at',
    url: 'https://rookoo.ai/trust/Data%20Processing%20Agreement%20Versie%201.1.pdf#toolbar=0&navpanes=0',
  },
];

const router = useRouter();
const store = useStore();
const { currentAccount, accountId } = useAccount();
const { t } = useI18n();

const activeTabIndex = ref(0);
const isAccepting = ref(false);
const pdfError = ref(false);

const customAttributes = computed(
  () => currentAccount.value.custom_attributes || {}
);
const activeDoc = computed(() => DOCS[activeTabIndex.value]);

const onTabChange = index => {
  activeTabIndex.value = index;
  pdfError.value = false;
};
const isActiveDocAccepted = computed(
  () => !!customAttributes.value[activeDoc.value.acceptedAttr]
);

const acceptDocument = async () => {
  isAccepting.value = true;
  try {
    await AccountAPI.acceptTerms(activeDoc.value.key);
    await store.dispatch('accounts/get', { id: currentAccount.value.id });
    useAlert(t('BILLING_SETTINGS.TERMS.ACCEPTED'));

    // Switch to the other doc (regardless of accepted state), or redirect if both done
    const otherIndex = activeTabIndex.value === 0 ? 1 : 0;
    const otherDoc = DOCS[otherIndex];
    if (!customAttributes.value[otherDoc.acceptedAttr]) {
      activeTabIndex.value = otherIndex;
      pdfError.value = false;
    } else {
      router.replace({
        name: 'billing_settings_index',
        params: { accountId: accountId.value },
      });
    }
  } catch (error) {
    const msg =
      error?.response?.data?.error || t('BILLING_SETTINGS.TERMS.ERROR');
    useAlert(msg);
  } finally {
    isAccepting.value = false;
  }
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TERMS.TITLE')"
        :description="$t('BILLING_SETTINGS.TERMS.SUBTITLE')"
        feature-name="billing"
      >
        <template #actions>
          <a
            :href="activeDoc.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex"
          >
            <ButtonV4 sm flushed slate icon="i-lucide-download">
              {{ $t('BILLING_SETTINGS.TERMS.DOWNLOAD') }}
            </ButtonV4>
          </a>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <section class="flex flex-col gap-4">
        <!-- Tabs -->
        <Tabs :index="activeTabIndex" @change="onTabChange">
          <TabsItem
            v-for="(doc, index) in DOCS"
            :key="doc.key"
            :index="index"
            :name="$t(doc.tabKey)"
            :show-badge="false"
            is-compact
          />
        </Tabs>

        <!-- PDF viewer -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden min-h-[60vh]"
        >
          <object
            v-if="!pdfError"
            :key="activeDoc.key"
            :data="activeDoc.url"
            type="application/pdf"
            class="w-full h-full min-h-[60vh]"
            @error="pdfError = true"
          >
            <div
              class="flex flex-col items-center justify-center h-full gap-4 p-8"
            >
              <Icon icon="i-lucide-file-text" class="text-n-slate-9 size-12" />
              <p class="text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.TERMS.PDF_FALLBACK') }}
              </p>
              <a
                :href="activeDoc.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm font-medium text-n-blue-11 hover:text-n-blue-12 underline"
              >
                {{ $t('BILLING_SETTINGS.TERMS.DOWNLOAD') }}
              </a>
            </div>
          </object>
          <div
            v-else
            class="flex flex-col items-center justify-center h-full gap-4 p-8"
          >
            <Icon icon="i-lucide-file-text" class="text-n-slate-9 size-12" />
            <p class="text-sm text-n-slate-11">
              {{ $t('BILLING_SETTINGS.TERMS.PDF_FALLBACK') }}
            </p>
            <a
              :href="activeDoc.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium text-n-blue-11 hover:text-n-blue-12 underline"
            >
              {{ $t('BILLING_SETTINGS.TERMS.DOWNLOAD') }}
            </a>
          </div>
        </div>

        <!-- Accept bar -->
        <div class="flex items-center justify-between gap-4">
          <p class="text-sm text-n-slate-11">
            {{ $t('BILLING_SETTINGS.TERMS.AGREEMENT_TEXT') }}
          </p>
          <div v-if="isActiveDocAccepted" class="flex items-center gap-1.5">
            <Icon icon="i-lucide-check-circle" class="text-n-teal-11 size-4" />
            <span class="text-sm font-medium text-n-teal-11">
              {{ $t('BILLING_SETTINGS.TERMS.ALREADY_ACCEPTED') }}
            </span>
          </div>
          <ButtonV4
            v-else
            md
            solid
            blue
            :is-loading="isAccepting"
            @click="acceptDocument"
          >
            {{ $t('BILLING_SETTINGS.TERMS.ACCEPT') }}
          </ButtonV4>
        </div>
      </section>
    </template>
  </SettingsLayout>
</template>
